import type { AnswerMode, RuleId } from '../data/mobileForwarding'
import type {
  DashboardState,
  ForwardingConnection,
  JourneyProgress,
  JourneyStart,
  RuleStatus,
} from '../types/forwarding'

const CONDITIONAL: RuleId[] = ['busy', 'unanswered', 'unreachable']

export const RULE_LABEL: Record<RuleId, string> = {
  busy: 'Busy',
  unanswered: 'No answer',
  unreachable: 'Unreachable',
  all: 'Every call',
}

export const STATUS_LABEL: Record<RuleStatus, string> = {
  verified: 'Active',
  reported: 'Active',
  needsSetup: 'Needs setup',
  failed: 'Failed',
  off: 'Off',
}

export const rulesForMode = (mode: AnswerMode): RuleId[] =>
  mode === 'all' ? ['all'] : CONDITIONAL

/** Configured rules for the current mode, in display order, with status. */
export function configuredRules(c: ForwardingConnection) {
  return rulesForMode(c.mode)
    .filter((r) => c.rules[r] !== undefined)
    .map((r) => ({ rule: r, status: c.rules[r] as RuleStatus }))
}

export const isOn = (s: RuleStatus) => s !== 'off'

/**
 * Derives the dashboard state from what's actually known. Never reports
 * "active" before setup was completed, and never treats a returning user
 * as first-time.
 */
export function deriveDashboardState(c: ForwardingConnection | null): DashboardState {
  if (!c) return 'firstTime'
  const rules = configuredRules(c)
  const on = rules.filter((r) => isOn(r.status))
  if (on.length === 0) return c.draft ? 'incomplete' : 'inactive'
  if (c.needsAttention) return 'attention'
  const done = rules.filter((r) => r.status === 'verified' || r.status === 'reported').length
  if (done === rules.length) return 'active'
  if (done > 0) return 'partial'
  return 'incomplete'
}

/** Rules that still need setup. */
export const rulesNeedingSetup = (c: ForwardingConnection) =>
  configuredRules(c)
    .filter((r) => r.status === 'needsSetup' || r.status === 'failed' || r.status === 'off')
    .map((r) => r.rule)

export const rulesWithStatus = (c: ForwardingConnection, s: RuleStatus) =>
  configuredRules(c)
    .filter((r) => r.status === s)
    .map((r) => r.rule)

export const activeRuleIds = (c: ForwardingConnection) =>
  configuredRules(c)
    .filter((r) => isOn(r.status))
    .map((r) => r.rule)

/** Saved "when I can't answer" preferences, even while answering every call. */
export function savedConditionalRules(c: ForwardingConnection): RuleId[] {
  const saved = CONDITIONAL.filter((r) => c.rules[r] !== undefined)
  return saved.length ? saved : CONDITIONAL
}

/** Rules to restore when turning call answering back on. */
export function reactivationStart(c: ForwardingConnection): JourneyStart {
  const saved = configuredRules(c).map((r) => r.rule)
  return {
    mode: c.mode,
    rules: saved.length ? saved : rulesForMode(c.mode),
    setup: c.setup,
    screen: 'mode',
  }
}

/** Resume an interrupted setup where it stopped. */
export function resumeStart(c: ForwardingConnection): JourneyStart {
  const pending = rulesNeedingSetup(c)
  const d = c.draft
  const screen = d && !['mode', 'fallback', 'switchConfirm', 'ready'].includes(d.screen) ? d.screen : 'method'
  return {
    mode: c.mode,
    rules: pending.length ? pending : activeRuleIds(c),
    setup: c.setup,
    screen,
    method: d?.method ?? c.setup?.method,
    codeStepIdx: d?.codeStepIdx ?? 0,
    settingsIdx: d?.settingsIdx ?? 0,
  }
}

/**
 * Folds a journey snapshot into the saved connection. Rules outside this run
 * keep their status; switching mode keeps the old mode's rules saved as off.
 */
export function mergeJourney(
  prev: ForwardingConnection | null,
  p: JourneyProgress,
  base: { numberType: 'mobile' | 'landline'; phoneNumber: string },
  finished: boolean,
): ForwardingConnection {
  // Keep every saved preference. When the mode changes, the old mode's rules
  // stay saved but are shown off — they're no longer what Vexu relies on.
  const rules: Partial<Record<RuleId, RuleStatus>> = prev ? { ...prev.rules } : {}
  if (prev && prev.mode !== p.mode) {
    rulesForMode(prev.mode).forEach((r) => {
      if (rules[r] !== undefined) rules[r] = 'off'
    })
  }
  const afterSetup = p.draft.screen === 'ready'
  p.rules.forEach((r) => {
    if (p.verification[r] === 'verified') rules[r] = 'verified'
    else if (afterSetup || finished) rules[r] = 'reported'
    else if (p.draft.screen === 'fallback') rules[r] = 'failed'
    else rules[r] = 'needsSetup'
  })
  const next: ForwardingConnection = {
    numberType: prev?.numberType ?? base.numberType,
    phoneNumber: prev?.phoneNumber ?? base.phoneNumber,
    mode: p.mode,
    rules,
    setup: p.setup,
    draft: finished ? null : p.draft,
    needsAttention: prev?.needsAttention ?? false,
  }
  // A clean re-verification of everything clears an earlier warning.
  if (next.needsAttention && configuredRules(next).every((r) => r.status === 'verified')) {
    next.needsAttention = false
  }
  return next
}

/** Mark every configured rule off, keeping the configuration for reactivation. */
export function turnAllOff(c: ForwardingConnection): ForwardingConnection {
  const rules: Partial<Record<RuleId, RuleStatus>> = {}
  configuredRules(c).forEach((r) => (rules[r.rule] = 'off'))
  return { ...c, rules, draft: null, needsAttention: false }
}

/** Remove one rule the user chose to stop using; if it was the last, keep it as off. */
export function turnRuleOff(c: ForwardingConnection, rule: RuleId): ForwardingConnection {
  const othersOn = activeRuleIds(c).filter((r) => r !== rule)
  if (othersOn.length === 0) return turnAllOff(c)
  const rules = { ...c.rules }
  delete rules[rule]
  return { ...c, rules }
}
